import { PrimitiveAtom, useSetAtom } from 'jotai';
import { Alert, AlertColor, IconButton, Box } from '@mui/material';
import { Close } from '@mui/icons-material';
import { SnackbarMessage } from '@/types';
import { snackbarMessage as analysisSnackbarMessageAtom } from '@/components/Analysis/state';
import { reportDownloadSnackbarMessage } from '@/state';

type Props = {
  atom?: PrimitiveAtom<SnackbarMessage>;
  variant?: 'filled' | 'standard' | 'outlined';
  isReportDownload?: boolean;
};

export const useSnackBarMessage = ({
  atom = analysisSnackbarMessageAtom,
  variant = 'filled',
  isReportDownload = false,
}: Props) => {
  const setSnackbarMessage = useSetAtom(isReportDownload ? reportDownloadSnackbarMessage : atom);
  const showSnackBar = (
    message: string,
    severity: AlertColor,
    options?: { autoHideDuration?: number | null; persistent?: boolean }
  ) => {
    const isPersistent = options?.persistent === true;

    setSnackbarMessage({
      shouldShow: true,
      content: (
        <Alert
          sx={{
            color: variant === 'standard' ? severity : 'white',
            ...(isPersistent && {
              '& .MuiAlert-message': {
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              },
            }),
          }}
          variant={variant}
          severity={severity}
        >
          {isPersistent ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <span>{message}</span>
              <IconButton
                size="small"
                onClick={() => setSnackbarMessage({ shouldShow: false, content: undefined })}
                sx={{
                  color: 'inherit',
                  padding: '4px',
                  marginLeft: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Close sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Box>
          ) : (
            message && (
              <>
                {message}
                &nbsp;&nbsp;
              </>
            )
          )}
        </Alert>
      ),
      ...(options?.autoHideDuration !== undefined && {
        duration: options.autoHideDuration ?? undefined,
      }),
      ...(options?.persistent !== undefined && { persistent: options.persistent }),
    });
  };

  return {
    showSnackBar,
  };
};
